/**
 * Batch Resync Hook
 * =================
 * Resyncs all assets in parallel on page load.
 * Tracks per-asset syncing state for per-row spinner indicators.
 */

import { useState, useCallback, useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { assetApi } from '@/services/api'
import { assetKeys } from '@/hooks/useAssets'
import type { Asset, AssetSearchParams, PaginatedResponse } from '@/types'

interface UseBatchResyncReturn {
  /** Set of asset IDs (numeric) currently being resynced */
  syncingIds: Set<number>
  /** Whether any resync is still in progress */
  isSyncing: boolean
  /** Number of assets completed so far */
  completedCount: number
  /** Total number of assets being resynced */
  totalCount: number
  /** Trigger batch resync for the given assets */
  startBatchResync: (assets: Asset[]) => void
}

export function useBatchResync(searchParams: AssetSearchParams): UseBatchResyncReturn {
  const queryClient = useQueryClient()
  const [syncingIds, setSyncingIds] = useState<Set<number>>(new Set())
  const [completedCount, setCompletedCount] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const abortRef = useRef(false)

  // Prevent state updates after unmount
  useEffect(() => {
    abortRef.current = false
    return () => { abortRef.current = true }
  }, [])

  const startBatchResync = useCallback((assets: Asset[]) => {
    if (assets.length === 0) return

    const ids = new Set(assets.map(a => a.id))
    setSyncingIds(ids)
    setTotalCount(assets.length)
    setCompletedCount(0)
    abortRef.current = false

    const promises = assets.map(async (asset) => {
      try {
        const response = await assetApi.refreshStatus(asset.id)

        if (abortRef.current) return

        // Surgically update this asset in the list cache
        if (response?.data) {
          const cacheKey = assetKeys.list(searchParams)
          queryClient.setQueryData<PaginatedResponse<Asset>>(cacheKey, (old) => {
            if (!old) return old
            return {
              ...old,
              data: old.data.map(a => a.id === asset.id ? response.data! : a),
            }
          })
        }
      } catch (error) {
        // Individual failures are OK — backend sets status to OFFLINE
        console.error(`Failed to resync asset ${asset.asset_id}:`, error)
      } finally {
        if (!abortRef.current) {
          setSyncingIds(prev => {
            const next = new Set(prev)
            next.delete(asset.id)
            return next
          })
          setCompletedCount(prev => prev + 1)
        }
      }
    })

    // Final consistency invalidation after all complete
    Promise.allSettled(promises).then(() => {
      if (!abortRef.current) {
        queryClient.invalidateQueries({ queryKey: assetKeys.lists() })
        queryClient.invalidateQueries({ queryKey: assetKeys.stats() })
      }
    })
  }, [queryClient, searchParams])

  return {
    syncingIds,
    isSyncing: syncingIds.size > 0,
    completedCount,
    totalCount,
    startBatchResync,
  }
}
